import { EntityType } from '@frontend/common';
import { CommonMessage, getCorrectTranslation, useLocale } from '@frontend/lang';
import { useModals } from '@frontend/modal';
import { ProductExtended, ProductType } from '@frontend/product/types';
import { Product } from '@frontend/product/utils';
import React from 'react';

import { CategoriesContainer } from '../../applications/modals/containers/categories-container';
import { CommonContainer } from '../../applications/modals/containers/common-container';
import { ConstraintsContainer } from '../../applications/modals/containers/constraints-container';
import { ImageContainerWrapper } from '../../applications/modals/containers/image-wrapper/image-container-wrapper.components';
import { TranslationContainer } from '../../applications/modals/containers/translation-container/translations-container.component';
import { EquipmentDetail } from '../../applications/modals/product-detail-modal/equipment-detail/equipment-detail.component';
import { KitItemsDetail } from '../../applications/modals/product-detail-modal/kit-items-detail/kit-items-detail.component';
import { StockDetail } from '../../applications/modals/product-detail-modal/stock-detail/stock-detail.component';
import { PlatformDetailGrid } from '../../modal/platform-detail-grid/platform-detail-grid.component';
import { useAppDispatch } from '../../redux/store';

export const ProductDetailsCard = ({ product }: { product: ProductExtended }) => {
    const { locale } = useLocale();
    return (
        <div className='flex w-full gap-3'>
            <div className='bg-background-surface outline-border-default inline-flex w-full flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
                <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.DETAIL.DETAILS}</div>
                <PlatformDetailGrid
                    object={product}
                    cols={2}
                    order={['sku', 'ean', 'product_name', 'type', 'description', 'user_info']}
                    valueOverwrite={
                        new Map<string, (obj: ProductExtended) => React.ReactNode>([
                            ['product_name', (obj) => (locale ? getCorrectTranslation(locale, obj, 'name') : obj.name)],
                            ['type', (obj) => new Product(obj).getTypeDisplayName()]
                        ])
                    }
                />
            </div>
            <ImageContainerWrapper
                entity={product}
                entity_type={EntityType.PRODUCT}
            />
        </div>
    );
};

export const ProductGeneralSection = ({ product }: { product: ProductExtended }) => (
    <>
        <div className='inline-flex w-full items-start justify-start gap-4 self-stretch'>
            <CommonContainer
                containerTitle={CommonMessage.OBJECTS.ATTRIBUTE.PLURAL}
                keyHeader={CommonMessage.OBJECTS.ATTRIBUTE.ATTRIBUTE_NAME}
                valueHeader={CommonMessage.OBJECTS.ATTRIBUTE_VALUE.VALUE}
                data={product.attributes}
            />
            <CategoriesContainer
                entity_id={product.id}
                entity_type={EntityType.PRODUCT}
                categories={product.categories}
            />
        </div>
        <TranslationContainer
            ContainerTitle={CommonMessage.DETAIL.TRANSLATIONS}
            HeaderList={[
                CommonMessage.OBJECTS.PRODUCT.PRODUCT_NAME,
                CommonMessage.OBJECTS.PRODUCT.PRODUCT_DESCRIPTION,
                CommonMessage.OBJECTS.PRODUCT.USER_INFO
            ]}
            translations={['name', 'description', 'user_info']}
            object={product}
        />
    </>
);

export const ProductStockSection = ({ product }: { product: ProductExtended }) => <StockDetail product={product} />;

export const ProductEquipmentsSection = ({ product }: { product: ProductExtended }) => <EquipmentDetail product={product} />;

export const ProductKitItemsSection = ({ product }: { product: ProductExtended }) => <KitItemsDetail product={product} />;

export const ProductConstraintsSection = ({ product }: { product: ProductExtended }) => {
    const dispatch = useAppDispatch();
    const { openModal } = useModals();
    return (
        <ConstraintsContainer
            currentAccount={product.account_id}
            dispatch={dispatch}
            entityType='product'
            entityId={product.id}
            entityName={product.name}
            productType={product.type}
            openModal={openModal}
        />
    );
};

export const isEquipmentProduct = (product: ProductExtended) => product.type === ProductType.EQUIPMENT;
export const isKitProduct = (product: ProductExtended) => product.type === ProductType.KIT;
